import {
  BOARD_SIZE,
  MAX_UNITS_PER_ATTACK,
  MAX_WAVES,
  UNIT_KINDS,
  type UnitKind,
} from "@empire/core-sim";
import { z } from "zod";
import { LayoutPayload } from "./layout.js";
import { WireResources } from "./village.js";
import { VillageEconomy } from "./village.js";

/**
 * Launching an attack and reading what came of it (S5-3, S5-4).
 *
 * Phase 0 resolves an attack the moment it is launched, so this contract
 * deviates from 05 in one visible way: `POST /attacks` answers with the battle
 * instead of with a marching movement and an arrival time. Marches, and the
 * scheduler that lands them, are Phase 2 (D-095).
 */

export const WireWave = z.object({
  /** Ticks after the first wave that this one enters. */
  offsetTicks: z.number().int().min(0),
  units: z
    .array(z.enum(UNIT_KINDS as unknown as [UnitKind, ...UnitKind[]]))
    .min(1)
    .max(MAX_UNITS_PER_ATTACK),
});

export const WirePlan = z.object({
  waves: z.array(WireWave).min(1).max(MAX_WAVES),
});
export type WirePlan = z.infer<typeof WirePlan>;

export const AttackRequest = z.object({
  fromVillageId: z.string().uuid(),
  toVillageId: z.string().uuid(),
  plan: WirePlan,
  /**
   * The client's own id for this attempt. A phone on a bad connection retries
   * a POST it never saw answered; without this, one tap costs two armies.
   */
  idempotencyKey: z.string().min(8).max(100),
});
export type AttackRequest = z.infer<typeof AttackRequest>;

/** What became of the attacker's army. Both sides see their own copy of this. */
export const BattleSummary = z.object({
  outcome: z.enum(["breach", "hold"]),
  ticks: z.number().int(),
  army: z.object({
    sent: z.number().int(),
    reached: z.number().int(),
    lost: z.number().int(),
    survived: z.number().int(),
  }),
  /**
   * What died, by unit kind — the bill for the attack (D-109).
   *
   * Defaulted rather than required because battles resolved before D-109 are
   * stored rows that will never have it, and a report a player can still open
   * must not fail to parse because the rules grew a field.
   */
  losses: z.record(z.number().int()).default({}),
  carryReaching: z.number().int(),
  /**
   * What the survivors actually carried home, per resource (D-118). Absent on
   * battles from before loot was real, and on any battle that took nothing.
   */
  loot: z
    .object({
      wood: z.number().int(),
      clay: z.number().int(),
      iron: z.number().int(),
      grain: z.number().int(),
    })
    .optional(),
  /** Why the loot is zero when survivors did reach the core. */
  lootNote: z.enum(["farmed", "empty", "protected"]).optional(),
  rulesVersion: z.string().optional(),
  deposited: WireResources.optional(),
  overflow: WireResources.optional(),
  replacementCost: WireResources.optional(),
});
export type BattleSummary = z.infer<typeof BattleSummary>;

/**
 * A layout as a viewer is allowed to see it.
 *
 * `tiles` is row-major and `null` means fog. Sending nulls rather than omitting
 * the tiles is deliberate: the client needs to draw "unknown" as a hatched
 * tile, and it must not be able to infer what is there from a gap.
 */
export const WireFogView = z.object({
  size: z.number().int(),
  gate: z.tuple([z.number().int(), z.number().int()]),
  wallLevel: z.number().int(),
  tiles: z
    .array(
      z
        .object({
          kind: z.string(),
          tower: z.object({ kind: z.string(), level: z.number().int() }).optional(),
        })
        .nullable()
    )
    .max(BOARD_SIZE * BOARD_SIZE),
  revealedTiles: z.number().int(),
  unknownCount: z.number().int(),
});
export type WireFogView = z.infer<typeof WireFogView>;

export const AttackResponse = z.object({
  battleId: z.string().uuid(),
  reportId: z.string().uuid(),
  resolvedAt: z.string(),
  simVersion: z.string(),
  result: BattleSummary,
  /** The attacker's view of the defence, already fogged server-side. */
  view: WireFogView,
  /** True when this request was a retry and nothing new was simulated. */
  replayed: z.boolean().default(false),
  /**
   * The attacking village, after the march (D-109).
   *
   * An attack now costs an army, so the answer carries the village it was paid
   * for out of — the same shape `GET /economy` returns. Without it the planner
   * would go on offering units that are dead until the next poll, and offering
   * them is how a player taps Attack and gets a refusal for no visible reason.
   */
  village: VillageEconomy,
});
export type AttackResponse = z.infer<typeof AttackResponse>;

export const ReportRow = z.object({
  id: z.string().uuid(),
  kind: z.enum(["battle_att", "battle_def"]),
  battleId: z.string().uuid().nullable(),
  title: z.string(),
  summary: BattleSummary.extend({
    opponent: z.string(),
    villageName: z.string(),
  }),
  read: z.boolean(),
  createdAt: z.string(),
});
export type ReportRow = z.infer<typeof ReportRow>;

export const ReportsResponse = z.object({
  reports: z.array(ReportRow),
  /** The `createdAt` to pass back as `before` for the next page, if any. */
  cursor: z.string().nullable(),
});
export type ReportsResponse = z.infer<typeof ReportsResponse>;

/** Positions of everything still walking, at one tick. */
export const WireFrame = z.object({
  tick: z.number().int(),
  /** Flat triples: unit id, x in milli-tiles, y in milli-tiles. */
  units: z.array(z.number().int()),
  /**
   * What happened this tick that is worth animating: flat quads of
   * `code, a, b, c`, defined by the FX_* codes in @empire/core-sim.
   *
   * Fogged, like everything else on this response. A tower the attacker never
   * earned the right to see does not appear here either — otherwise a replay
   * would draw a shot arriving from an empty square, which is a more precise
   * leak than sending the tower would have been. Optional and defaulted so a
   * client built before this existed still parses a reply that has it.
   */
  fx: z.array(z.number().int()).default([]),
  /**
   * Health of each unit in `units`, same order, in thousandths (0..1000).
   * The attacker's own army, so there is nothing here to fog. Optional and
   * defaulted for the same reason as `fx`.
   */
  hp: z.array(z.number().int()).default([]),
});
export type WireFrame = z.infer<typeof WireFrame>;

export const ReplayResponse = z.object({
  battleId: z.string().uuid(),
  simVersion: z.string(),
  seed: z.number().int(),
  plan: WirePlan,
  /**
   * The defence, in full — and therefore ONLY for a viewer who is allowed all
   * of it: the defender, or an attacker who broke through. Everyone else gets
   * null here and watches `frames` instead. See the note on `frames`.
   */
  layout: LayoutPayload.nullable(),
  view: WireFogView,
  /**
   * A recorded stream, for a viewer who may not have the layout.
   *
   * 02 GDD §8 says a replay is the INPUT and the client re-simulates it. That
   * works for the defender, who already knows their own maze. It cannot work
   * for a fogged attacker: a fogged layout has a truncated road, which the
   * simulation cannot walk at all — and sending the untruncated one would put
   * the whole defence on the attacker's device, which is the one thing the fog
   * rule exists to prevent. So the server re-runs the battle from the stored
   * inputs and sends the positions instead (D-096). Null for the defender,
   * who does not need it.
   */
  frames: z.array(WireFrame).nullable(),
  result: BattleSummary,
  resultHash: z.string(),
  signature: z.string(),
});
export type ReplayResponse = z.infer<typeof ReplayResponse>;

export const DesyncReport = z.object({
  clientHash: z.string().min(1).max(64),
  platform: z.string().max(40),
  simVersion: z.string().max(40),
});
export type DesyncReport = z.infer<typeof DesyncReport>;

export const TelemetryBatch = z.object({
  events: z
    .array(
      z.object({
        name: z.string().min(1).max(80),
        props: z.record(z.unknown()).optional(),
        ts: z.string().optional(),
      })
    )
    .min(1)
    .max(50),
});
export type TelemetryBatch = z.infer<typeof TelemetryBatch>;

/** A tester you can attack (S5-5). No map yet, so this is the whole world. */
export const TargetRow = z.object({
  villageId: z.string().uuid(),
  villageName: z.string(),
  playerName: z.string(),
  x: z.number().int(),
  y: z.number().int(),
  /** How many times this defence has been attacked, by anyone. */
  attackedCount: z.number().int(),
  /** Whether YOU have already broken through it. */
  breachedByMe: z.boolean(),
  layoutChangedAt: z.string().nullable(),
  /**
   * Set on a barbarian village (D-113): owned by nobody, always there, with a
   * difficulty so a new player can pick a fight they can win. Null for a
   * player's village. Optional so an older server's answer still parses.
   */
  difficulty: z.enum(["easy", "medium", "hard", "brutal"]).nullable().default(null),
  /** Beginner's protection (D-118): when it ends, or null if the village can be attacked now. */
  protectedUntil: z.string().nullable().default(null),
  attackBlockedUntil: z.string().nullable().optional(),
  attackBlockReason: z.string().nullable().optional(),
  rewardNote: z.string().nullable().optional(),
  progressionTier: z.number().int().positive().optional(),
});
export type TargetRow = z.infer<typeof TargetRow>;

export const TargetsResponse = z.object({
  targets: z.array(TargetRow),
  /**
   * Where the asking player's own village is, so the map can centre on it.
   * Optional so an older server's answer still parses.
   */
  home: z.object({ x: z.number().int(), y: z.number().int() }).nullable().default(null),
});

/**
 * What you have learned about one village, merged across every attack (S3-2).
 *
 * Intel is only counted from attacks made SINCE the defender last changed their
 * maze. A tower you saw last week is not information about the maze standing
 * today, and showing it as if it were would be worse than showing nothing — the
 * planner would be confidently wrong rather than honestly empty.
 */
export const IntelResponse = z.object({
  villageId: z.string().uuid(),
  /** Null when you have never usefully attacked this village. */
  view: WireFogView.nullable(),
  /** Attacks that still count, because the maze has not changed since. */
  battles: z.number().int(),
  /** Attacks whose intel was thrown away because the defender rebuilt. */
  staleBattles: z.number().int(),
  lastAttackAt: z.string().nullable(),
  /** You broke through at least once, so you have seen all of it. */
  breached: z.boolean(),
  layoutChangedAt: z.string().nullable(),
});
export type IntelResponse = z.infer<typeof IntelResponse>;
export type TargetsResponse = z.infer<typeof TargetsResponse>;
